import { useEffect, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProChip } from "@/components/billing/PlanLinks";
import { api, errMsg } from "@/lib/api";

const SOURCE = { admin: "Admin", granted: "Granted", subscription: "Paid", trial: "Trial", referral: "Invite reward" };
const FILTERS = [["all", "All"], ["free", "Free"], ["pro", "Pro"], ["commercial", "Commercial"], ["trial", "Trial"], ["beta", "Beta testers"], ["test", "Test accounts"], ["unconfirmed", "Unconfirmed"]];

function UserRow({ u, onGrant, onBeta, checked, onCheck }) {
  const locked = u.base_source === "admin";
  const isCustom = u.base_source === "granted" && u.base_features?.length > 0 && u.base_features.length < 4;
  return (
    <tr className="border-t border-[#2D3442]" data-testid={`admin-user-${u.id}`}>
      <td className="py-3 pr-3"><Checkbox checked={checked} disabled={!u.deletable} onCheckedChange={(v) => onCheck(u.id, !!v)} data-testid={`admin-select-${u.id}`} /></td>
      <td className="py-3 pr-4"><div className="text-[#F3EBDD]">{u.name || "—"}</div><div className="text-xs text-[#A8A090]">{u.email}</div>
        <label className="mt-1 flex w-fit cursor-pointer items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#C4B5FD]"><Checkbox checked={!!u.beta} disabled={u.is_admin} onCheckedChange={(v) => onBeta(u, !!v)} className="h-3.5 w-3.5" data-testid={`admin-beta-${u.id}`} />Beta tester</label></td>
      <td className="py-3 pr-4 text-sm text-[#A8A090]">{u.universes}</td>
      <td className="py-3 pr-4 text-sm text-[#A8A090]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</td>
      <td className="py-3 pr-4" data-testid={`admin-user-plan-${u.id}`}>{u.base_source ? <span className="flex items-center gap-2"><ProChip />{u.base_tier === "commercial" ? "Commercial · " : ""}{SOURCE[u.base_source]}</span> : <span className="text-sm text-[#A8A090]">Free{u.source === "event" ? " · event Pro" : ""}</span>}
        {u.base_features?.length > 0 && u.base_features.length < 4 && <div className="text-[10px] text-[#A8A090]" data-testid={`admin-user-features-${u.id}`}>{u.base_features.map((f) => FEATS.find(([k]) => k === f)?.[1]).join(" · ")}</div>}
        {u.subscription?.status && u.base_source !== "subscription" && <div className="text-[10px] text-[#A8A090]">Stripe: {u.subscription.status}</div>}
        {u.base_source === "trial" && <div className="text-[10px] text-[#A8A090]">ends {new Date(u.trial_ends_at).toLocaleDateString()}</div>}
        {!u.email_verified && <div className="text-[10px] text-[#F87171]" data-testid={`admin-user-unverified-${u.id}`}>Email not confirmed</div>}</td>
      <td className="py-3 text-right">
        <Select value={locked ? "commercial" : isCustom ? "custom" : u.base_source === "granted" ? u.base_tier : "none"} disabled={locked} onValueChange={(v) => onGrant(u, v)}>
          <SelectTrigger className="ml-auto h-8 w-36" data-testid={`admin-grant-${u.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" data-testid={`admin-grant-none-${u.id}`}>No grant</SelectItem>
            <SelectItem value="pro" data-testid={`admin-grant-pro-${u.id}`}>Grant Pro</SelectItem>
            <SelectItem value="commercial" data-testid={`admin-grant-commercial-${u.id}`}>Grant Commercial</SelectItem>
            <SelectItem value="custom" data-testid={`admin-grant-custom-${u.id}`}>{isCustom ? `Custom (${u.base_features.length})` : "Grant custom…"}</SelectItem>
            {isCustom && <SelectItem value="custom-edit" data-testid={`admin-grant-custom-edit-${u.id}`}>Edit custom…</SelectItem>}
          </SelectContent>
        </Select>
      </td>
    </tr>
  );
}

const FEATS = [["timelines", "Timelines & Calendars"], ["maps", "Maps"], ["relationships", "Relationships & Family Trees"], ["languages", "Languages"]];

function CustomGrantDialog({ user, onClose, onSave }) {
  const [sel, setSel] = useState(new Set(user?.base_features?.length < 4 ? user.base_features : []));
  const [commercial, setCommercial] = useState(user?.base_tier === "commercial" && user?.base_features?.length < 4);
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-custom-grant-dialog">
        <DialogHeader><DialogTitle>Custom grant for {user?.email}</DialogTitle>
          <DialogDescription>Pick the Pro features this account gets for free. Universes they own unlock the same features for their members.</DialogDescription></DialogHeader>
        <div className="space-y-2.5">
          {FEATS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2.5 text-sm text-[#F3EBDD]">
              <Checkbox checked={sel.has(k)} onCheckedChange={(v) => setSel((s) => { const n = new Set(s); v ? n.add(k) : n.delete(k); return n; })} data-testid={`admin-custom-${k}`} />{l}
            </label>
          ))}
          <label className="flex items-center gap-2.5 border-t border-[#2D3442] pt-3 text-sm text-[#D9D0C1]">
            <Checkbox checked={commercial} onCheckedChange={(v) => setCommercial(!!v)} data-testid="admin-custom-commercial" />Include commercial-use license
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!sel.size} onClick={() => onSave(user, [...sel], commercial)} data-testid="admin-custom-save">Grant features</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrialForm() {
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(14);
  const qc = useQueryClient();
  const go = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/trial", { email, days: Number(days) }); toast.success(`${data.email} has full Pro until ${new Date(data.trial_ends_at).toLocaleDateString()}`); setEmail(""); qc.invalidateQueries({ queryKey: ["admin-users"] }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={go} className="mb-6 flex flex-wrap items-end gap-3 rounded-md border border-[#2D3442] bg-[#0B0D10]/60 p-4" data-testid="admin-trial-form">
      <div className="text-xs uppercase tracking-[0.16em] text-[#A8A090] w-full">Grant a timed Pro trial</div>
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="writer@example.com" className="w-64" data-testid="admin-trial-email" />
      <label className="flex items-center gap-2 text-xs text-[#A8A090]"><Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="w-20" data-testid="admin-trial-days" />days</label>
      <Button type="submit" size="sm" data-testid="admin-trial-submit">Grant trial</Button>
    </form>
  );
}

function DeleteDialog({ open, onOpenChange, count, label, onConfirm }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);
  const go = async () => { setBusy(true); try { await onConfirm(); onOpenChange(false); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete {count} {count === 1 ? "account" : "accounts"}?</DialogTitle>
          <DialogDescription>{label}. Their universes and everything in them will be permanently deleted, and any Stripe subscriptions cancelled. Admin accounts and your own account are always skipped. This can't be undone.</DialogDescription>
        </DialogHeader>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type DELETE to confirm" data-testid="admin-delete-confirm-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="admin-delete-cancel">Cancel</Button>
          <Button className="bg-[#B91C1C] text-white hover:bg-[#991B1B]" disabled={text !== "DELETE" || busy} onClick={go} data-testid="admin-delete-confirm-button">Delete permanently</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const AccountsPanel = ({ onCounts }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customFor, setCustomFor] = useState(null);
  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [debounced, filter]);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", debounced, filter, page], placeholderData: keepPreviousData,
    queryFn: () => api.get("/admin/users", { params: { q: debounced, filter, page, per_page: 25 } }).then((r) => r.data),
  });
  const items = data?.items || [];
  useEffect(() => { if (data && filter === "all" && !debounced) onCounts?.(data.total); }, [data, filter, debounced, onCounts]);

  const setBeta = async (u, beta) => {
    try { await api.post(`/admin/users/${u.id}/beta`, { beta }); toast.success(beta ? `${u.email} can now use beta features` : `${u.email} removed from beta`); qc.invalidateQueries({ queryKey: ["admin-users"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const grant = async (u, tier, features = [], commercial = false) => {
    if (tier === "custom-edit" || (tier === "custom" && !features.length)) return setCustomFor(u);
    try { await api.post(`/admin/users/${u.id}/pro`, { tier, features, commercial }); setCustomFor(null); toast.success(tier === "none" ? `Removed the grant from ${u.email}` : `Granted ${tier === "custom" ? "custom features" : tier === "pro" ? "Pro" : "Commercial"} to ${u.email}`); qc.invalidateQueries({ queryKey: ["admin-users"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const check = (id, v) => { setAllMatching(false); setSelected((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; }); };
  const pageIds = items.filter((u) => u.deletable).map((u) => u.id);
  const pageAll = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = (v) => { setAllMatching(false); setSelected((s) => { const n = new Set(s); pageIds.forEach((id) => (v ? n.add(id) : n.delete(id))); return n; }); };
  const count = allMatching ? data?.total || 0 : selected.size;
  const canMatchAll = (debounced.trim() || filter !== "all") && (data?.total || 0) > selected.size;

  const doDelete = async () => {
    try {
      const body = allMatching ? { confirm: "DELETE", match_q: debounced, match_filter: filter } : { confirm: "DELETE", ids: [...selected] };
      const { data: r } = await api.post("/admin/users/delete", body);
      toast.success(`Deleted ${r.deleted} accounts${r.subscriptions_cancelled ? `, cancelled ${r.subscriptions_cancelled} subscriptions` : ""}`);
      setSelected(new Set()); setAllMatching(false); setPage(1);
      qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-audience"] });
    } catch (e) { toast.error(errMsg(e)); throw e; }
  };

  return (
    <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="admin-accounts">
      <TrialForm />
      <CustomGrantDialog key={customFor?.id || "none"} user={customFor} onClose={() => setCustomFor(null)} onSave={(u, f, c) => grant(u, "custom", f, c)} />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A090]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email (e.g. resend.dev)" className="pl-9" data-testid="admin-user-search" />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="admin-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => { setFilter(k); setPage(1); }} data-testid={`admin-filter-${k}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === k ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#FBBF24]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {count > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-[#B91C1C]/40 bg-[#B91C1C]/10 px-4 py-2.5 text-sm" data-testid="admin-selection-bar">
          <span className="text-[#F3EBDD]" data-testid="admin-selection-count">{allMatching ? `All ${count} matching accounts selected` : `${count} selected`}</span>
          {!allMatching && canMatchAll && <button onClick={() => setAllMatching(true)} className="text-[#F59E0B] hover:underline" data-testid="admin-select-all-matching">Select all {data.total} matching</button>}
          <button onClick={() => { setSelected(new Set()); setAllMatching(false); }} className="text-[#A8A090] hover:text-[#F3EBDD]" data-testid="admin-clear-selection">Clear</button>
          <Button size="sm" className="ml-auto bg-[#B91C1C] text-white hover:bg-[#991B1B]" onClick={() => setConfirmOpen(true)} data-testid="admin-delete-button"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button>
        </div>
      )}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[720px] text-left" data-testid="admin-users-table">
          <thead><tr className="text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">
            <th className="w-8 pb-2"><Checkbox checked={pageAll} onCheckedChange={(v) => togglePage(!!v)} data-testid="admin-select-page" /></th>
            <th className="pb-2">Account</th><th className="pb-2">Universes</th><th className="pb-2">Joined</th><th className="pb-2">Plan</th><th className="pb-2 text-right">Grant plan</th></tr></thead>
          <tbody>{items.map((u) => <UserRow key={u.id} u={u} onGrant={grant} onBeta={setBeta} checked={allMatching ? u.deletable : selected.has(u.id)} onCheck={check} />)}</tbody>
        </table>
        {isLoading && <p className="py-6 text-sm text-[#A8A090]">Loading accounts…</p>}
        {data && !items.length && <p className="py-6 text-sm text-[#A8A090]" data-testid="admin-no-results">No accounts match.</p>}
      </div>
      {data && data.pages > 1 && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="admin-pagination">
          <span className="text-xs text-[#A8A090]" data-testid="admin-page-info">{(data.page - 1) * data.per_page + 1}–{Math.min(data.page * data.per_page, data.total)} of {data.total}</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
            {Array.from({ length: data.pages }, (_, i) => i + 1).filter((n) => n === 1 || n === data.pages || Math.abs(n - page) <= 2).map((n, i, arr) => (
              <span key={n} className="flex items-center">
                {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-[#7A7468]">…</span>}
                <button onClick={() => setPage(n)} data-testid={`admin-page-${n}`}
                  className={`h-8 min-w-8 rounded-md px-2 text-xs ${n === page ? "bg-[#F59E0B] text-[#0B0D10]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>{n}</button>
              </span>
            ))}
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="admin-page-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <DeleteDialog open={confirmOpen} onOpenChange={setConfirmOpen} count={count}
        label={allMatching ? `Every account matching ${debounced ? `"${debounced}"` : "this filter"}${filter !== "all" ? ` (${filter})` : ""}` : "The accounts you ticked"} onConfirm={doDelete} />
    </section>
  );
};
